(() => {
  if (customElements.get('ci-store-locator')) return;

  const BRAZIL_STATES = {
    AC: 'acre',
    AL: 'alagoas',
    AP: 'amapa',
    AM: 'amazonas',
    BA: 'bahia',
    CE: 'ceara',
    DF: 'distrito federal',
    ES: 'espirito santo',
    GO: 'goias',
    MA: 'maranhao',
    MT: 'mato grosso',
    MS: 'mato grosso do sul',
    MG: 'minas gerais',
    PA: 'para',
    PB: 'paraiba',
    PR: 'parana',
    PE: 'pernambuco',
    PI: 'piaui',
    RJ: 'rio de janeiro',
    RN: 'rio grande do norte',
    RS: 'rio grande do sul',
    RO: 'rondonia',
    RR: 'roraima',
    SC: 'santa catarina',
    SP: 'sao paulo',
    SE: 'sergipe',
    TO: 'tocantins',
  };

  class CIStoreLocator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.form = this.querySelector('[data-locator-form]');
      this.input = this.querySelector('[data-cep-input]');
      this.submitButton = this.querySelector('[data-submit-button]');
      this.submitLabel = this.querySelector('[data-submit-label]');
      this.status = this.querySelector('[data-status]');
      this.searchPanel = this.querySelector('[data-search-panel]');
      this.resultsPanel = this.querySelector('[data-results-panel]');
      this.resultsTitle = this.querySelector('[data-results-title]');
      this.resultsSubtitle = this.querySelector('[data-results-subtitle]');
      this.resultsTrack = this.querySelector('[data-results-track]');
      this.resultsDots = this.querySelector('[data-results-dots]');
      this.newSearchButton = this.querySelector('[data-new-search]');
      this.template = this.querySelector('[data-result-card-template]');
      this.defaultSubmitLabel = this.submitLabel?.textContent || 'Buscar';
      this.maxResults = this.readNumber(this.dataset.maxResults, 5);
      this.locationTimeout = this.readNumber(this.dataset.locationTimeout, 8000);
      this.locations = this.readLocations();
      this.locationsLoadError = null;
      this.locationsReady = this.loadRemainingLocations().catch((error) => {
        this.locationsLoadError = error;
      });
      this.dragState = {
        active: false,
        dragged: false,
        pointerId: null,
        scrollLeft: 0,
        startX: 0,
      };
      this.queuedDotSync = false;
      this.suppressNextClick = false;

      this.form?.addEventListener('submit', this.handleSubmit.bind(this));
      this.input?.addEventListener('input', this.handleCepInput.bind(this));
      this.newSearchButton?.addEventListener('click', this.resetSearch.bind(this));
      this.resultsTrack?.addEventListener('scroll', this.requestDotSync.bind(this), { passive: true });
      this.resultsTrack?.addEventListener('pointerdown', this.startDrag.bind(this));
      this.resultsTrack?.addEventListener('pointermove', this.moveDrag.bind(this));
      this.resultsTrack?.addEventListener('pointerup', this.endDrag.bind(this));
      this.resultsTrack?.addEventListener('pointercancel', this.endDrag.bind(this));
      this.resultsTrack?.addEventListener('lostpointercapture', this.endDrag.bind(this));
      this.resultsTrack?.addEventListener('click', this.handleTrackClick.bind(this), true);
      window.addEventListener('resize', this.handleResize.bind(this));
    }

    readNumber(value, fallback) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    }

    readLocations(script = this.querySelector('[data-locations-json]')) {
      if (!script) return [];

      try {
        const data = JSON.parse(script.textContent || '[]');

        return data
          .filter((location) => location && typeof location === 'object')
          .map((location) => ({
            state: this.toText(location.state),
            city: this.toText(location.city),
            neighborhood: this.toText(location.neighborhood),
            address: this.toText(location.address),
            name: this.toText(location.name),
            cep: this.onlyDigits(location.cep),
            latitude: this.toCoordinate(location.latitude),
            longitude: this.toCoordinate(location.longitude),
            document: this.toText(location.document),
          }))
          .filter((location) => location.name || location.address || location.cep);
      } catch (error) {
        return [];
      }
    }

    async loadRemainingLocations() {
      let locationsScript = this.querySelector('[data-locations-json]');
      let nextPageUrl = locationsScript?.dataset.nextPageUrl;
      const requestedPages = new Set();

      while (nextPageUrl) {
        const requestUrl = new URL(nextPageUrl, window.location.origin);

        requestUrl.searchParams.set('section_id', this.dataset.sectionId);

        const pageUrl = requestUrl.toString();
        if (requestedPages.has(pageUrl)) throw new Error('Repeated locations page');

        requestedPages.add(pageUrl);

        const response = await fetch(pageUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });

        if (!response.ok) throw new Error('Locations page request failed');

        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        locationsScript = documentFragment.querySelector('[data-locations-json]');

        if (!locationsScript) throw new Error('Locations page not found');

        this.locations.push(...this.readLocations(locationsScript));
        nextPageUrl = locationsScript.dataset.nextPageUrl;
      }
    }

    handleCepInput(event) {
      event.target.value = this.formatCep(event.target.value);
    }

    async handleSubmit(event) {
      event.preventDefault();

      const cep = this.onlyDigits(this.input?.value);

      if (cep.length !== 8) {
        this.setStatus('Digite um CEP válido com 8 números.');
        this.input?.focus();
        return;
      }

      this.setLoading(true);
      this.setStatus('Carregando unidades...');

      try {
        await this.locationsReady;

        if (this.locationsLoadError) {
          this.setStatus('Não foi possível carregar todas as unidades. Tente novamente.');
          return;
        }

        if (!this.locations.length) {
          this.setStatus('Nenhuma unidade foi cadastrada no metacampo global.');
          return;
        }

        this.setStatus('Conferindo o CEP...');

        const cepData = await this.fetchCep(cep);
        const cepCoordinates = this.getCepCoordinates(cepData);

        if (cepCoordinates) {
          this.setStatus('Calculando as unidades mais próximas do CEP...');

          const results = this.findByDistance(cepCoordinates);

          if (results.length) {
            this.renderResults(results, 'location');
            return;
          }

          this.renderCepResults(cep, cepData);
          return;
        }

        this.setStatus(
          'Não encontramos as coordenadas exatas deste CEP. Autorize sua localização para encontrar as unidades mais próximas.'
        );
        await this.waitForStatusUpdate();

        let position = null;

        try {
          position = await this.requestPosition();
        } catch (error) {
          position = null;
        }

        if (position) {
          const results = this.findByDistance(position.coords);

          if (results.length) {
            this.renderResults(results, 'location');
            return;
          }
        }

        this.setStatus('Buscando unidades por cidade e bairro...');
        this.renderCepResults(cep, cepData);
      } catch (error) {
        this.setStatus(this.dataset.errorMessage || 'Não foi possível concluir a busca. Confira o CEP e tente novamente.');
      } finally {
        this.setLoading(false);
      }
    }

    requestPosition() {
      if (!navigator.geolocation) return Promise.resolve(null);

      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          maximumAge: 300000,
          timeout: this.locationTimeout,
        });
      });
    }

    getCepCoordinates(cepData) {
      const latitude = this.toCoordinate(cepData?.latitude);
      const longitude = this.toCoordinate(cepData?.longitude);

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

      return { latitude, longitude };
    }

    waitForStatusUpdate() {
      return new Promise((resolve) => {
        window.requestAnimationFrame(() => window.setTimeout(resolve, 0));
      });
    }

    findByDistance(coords) {
      const latitude = this.toCoordinate(coords.latitude);
      const longitude = this.toCoordinate(coords.longitude);

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return [];

      return this.locations
        .filter((location) => Number.isFinite(location.latitude) && Number.isFinite(location.longitude))
        .map((location) => ({
          ...location,
          distance: this.calculateDistance(latitude, longitude, location.latitude, location.longitude),
        }))
        .sort((a, b) => a.distance - b.distance)
        .slice(0, this.maxResults);
    }

    renderCepResults(cep, cepData) {
      const results = this.findByCep(cep, cepData).slice(0, this.maxResults);

      if (!results.length) {
        this.setStatus(this.dataset.emptyMessage || 'Não encontramos unidades para esse CEP.');
        return;
      }

      this.renderResults(results, 'cep');
    }

    async fetchCep(cep) {
      try {
        const response = await fetch(`https://brasilapi.com.br/api/cep/v2/${cep}`);

        if (response.ok) {
          const data = await response.json();

          if (data?.cep) {
            return {
              cep: data.cep,
              uf: data.state,
              estado: data.state,
              localidade: data.city,
              bairro: data.neighborhood,
              logradouro: data.street,
              latitude: data.location?.coordinates?.latitude,
              longitude: data.location?.coordinates?.longitude,
            };
          }
        }
      } catch (error) {
        // ViaCEP remains available as an address-only fallback.
      }

      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);

      if (!response.ok) throw new Error('CEP request failed');

      const data = await response.json();

      if (!data || data.erro) throw new Error('CEP not found');

      return data;
    }

    findByCep(cep, cepData) {
      const stateLocations = this.filterByCepState(this.locations, cepData);
      const exactCepMatches = stateLocations.filter((location) => location.cep === cep);

      if (exactCepMatches.length) return this.sortByName(exactCepMatches);

      const targetState = this.normalizeState(cepData.uf, cepData.estado);
      const targetCity = this.normalize(cepData.localidade);
      const targetNeighborhood = this.normalize(cepData.bairro);

      return this.sortByName(
        stateLocations.filter((location) => {
          const locationState = this.normalizeState(location.state);
          const locationCity = this.normalize(location.city);
          const locationNeighborhood = this.normalize(location.neighborhood);
          const stateMatches = !targetState || !locationState || targetState === locationState;
          const cityMatches = !targetCity || !locationCity || targetCity === locationCity;

          if (!stateMatches || !cityMatches) return false;
          if (!targetNeighborhood) return true;

          return (
            locationNeighborhood === targetNeighborhood ||
            locationNeighborhood.includes(targetNeighborhood) ||
            targetNeighborhood.includes(locationNeighborhood)
          );
        })
      );
    }

    filterByCepState(locations, cepData) {
      const targetState = this.normalizeState(cepData?.uf, cepData?.estado);

      if (!targetState) return [];

      return locations.filter((location) => this.normalizeState(location.state) === targetState);
    }

    renderResults(results, mode) {
      const limitedResults = results.slice(0, this.maxResults);

      this.resultsTrack.innerHTML = '';
      limitedResults.forEach((location) => this.resultsTrack.appendChild(this.createResultCard(location)));

      const titleTemplate =
        mode === 'cep'
          ? this.dataset.cepResultTitle || this.dataset.resultTitle
          : this.dataset.resultTitle || 'Encontramos [count] unidades perto de você';

      this.resultsTitle.textContent = titleTemplate.replace('[count]', limitedResults.length);
      this.resultsSubtitle.textContent =
        this.dataset.resultSubtitle || 'Veja as opções mais próximas e escolha a melhor opção para você';

      this.searchPanel.hidden = true;
      this.resultsPanel.hidden = false;
      this.resultsTrack.scrollLeft = 0;
      this.renderDots();
      this.setStatus('');
      this.resultsPanel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.requestAnimationFrame(() => {
        this.renderDots();
        this.syncDots();
      });
    }

    createResultCard(location) {
      const card = this.template.content.firstElementChild.cloneNode(true);
      const name = location.name || 'Unidade';
      const address = this.formatAddress(location);

      card.querySelector('[data-result-name]').textContent = name;
      card.querySelector('[data-result-address]').textContent = address;
      card.querySelector('[data-result-distance]').textContent = Number.isFinite(location.distance)
        ? `${this.formatDistance(location.distance)} de distância`
        : '';

      return card;
    }

    renderDots() {
      const pages = this.getCarouselPages();

      this.carouselPages = pages;
      this.resultsDots.innerHTML = '';
      this.resultsDots.hidden = pages.length <= 1;

      if (pages.length <= 1) return;

      pages.forEach((page, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ci-store-locator__dot';
        dot.setAttribute('aria-label', `Ir para página ${index + 1}`);
        dot.addEventListener('click', () => this.scrollToPage(index));
        this.resultsDots.appendChild(dot);
      });
    }

    getCarouselPages() {
      if (!this.resultsTrack) return [];

      const maxScroll = Math.max(0, this.resultsTrack.scrollWidth - this.resultsTrack.clientWidth);

      if (maxScroll <= 1) return [];

      const pageSize = Math.max(1, this.resultsTrack.clientWidth);
      const pages = [0];

      for (let position = pageSize; position < maxScroll; position += pageSize) {
        pages.push(Math.round(position));
      }

      pages.push(Math.round(maxScroll));

      return pages.filter((page, index) => index === 0 || Math.abs(page - pages[index - 1]) > 8);
    }

    scrollToPage(index) {
      const pages = this.carouselPages?.length ? this.carouselPages : this.getCarouselPages();
      const left = pages[index];

      if (!Number.isFinite(left)) return;

      this.resultsTrack.scrollTo({
        left,
        behavior: 'smooth',
      });
    }

    requestDotSync() {
      if (this.queuedDotSync) return;

      this.queuedDotSync = true;

      window.requestAnimationFrame(() => {
        this.queuedDotSync = false;
        this.syncDots();
      });
    }

    syncDots() {
      if (!this.resultsDots || this.resultsDots.hidden) return;

      const dots = Array.from(this.resultsDots.children);
      const pages = this.carouselPages?.length ? this.carouselPages : this.getCarouselPages();

      if (!dots.length || !pages.length) return;

      const activeIndex = pages.reduce(
        (closest, page, index) => {
          const distance = Math.abs(page - this.resultsTrack.scrollLeft);
          return distance < closest.distance ? { index, distance } : closest;
        },
        { index: 0, distance: Infinity }
      ).index;

      dots.forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    handleResize() {
      if (!this.resultsPanel || this.resultsPanel.hidden) return;

      this.renderDots();
      this.requestDotSync();
    }

    startDrag(event) {
      if (!this.resultsTrack || event.pointerType !== 'mouse' || event.button !== 0) return;
      if (this.resultsTrack.scrollWidth <= this.resultsTrack.clientWidth) return;

      this.dragState = {
        active: true,
        dragged: false,
        pointerId: event.pointerId,
        scrollLeft: this.resultsTrack.scrollLeft,
        startX: event.clientX,
      };

      this.resultsTrack.classList.add('is-dragging');
      this.resultsTrack.setPointerCapture?.(event.pointerId);
    }

    moveDrag(event) {
      if (!this.dragState.active || event.pointerId !== this.dragState.pointerId) return;

      const delta = event.clientX - this.dragState.startX;

      if (Math.abs(delta) > 4) {
        this.dragState.dragged = true;
        event.preventDefault();
      }

      this.resultsTrack.scrollLeft = this.dragState.scrollLeft - delta;
      this.requestDotSync();
    }

    endDrag(event) {
      if (!this.dragState.active || event.pointerId !== this.dragState.pointerId) return;

      const dragged = this.dragState.dragged;

      this.dragState.active = false;
      this.resultsTrack.classList.remove('is-dragging');

      try {
        this.resultsTrack.releasePointerCapture?.(event.pointerId);
      } catch (error) {
        // Pointer capture may already be released by the browser.
      }

      if (dragged) {
        this.suppressNextClick = true;
        window.setTimeout(() => {
          this.suppressNextClick = false;
        }, 0);
      }

      this.requestDotSync();
    }

    handleTrackClick(event) {
      if (!this.suppressNextClick) return;

      event.preventDefault();
      event.stopPropagation();
      this.suppressNextClick = false;
    }

    resetSearch() {
      this.resultsPanel.hidden = true;
      this.searchPanel.hidden = false;
      this.resultsTrack.innerHTML = '';
      this.resultsDots.innerHTML = '';
      this.setStatus('');
      this.input?.focus();
      this.searchPanel.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    setLoading(isLoading) {
      if (!this.submitButton || !this.submitLabel) return;

      this.submitButton.disabled = isLoading;
      this.submitLabel.textContent = isLoading ? 'Buscando...' : this.defaultSubmitLabel;
    }

    setStatus(message) {
      if (!this.status) return;

      this.status.textContent = message;
    }

    formatAddress(location) {
      const region = [location.neighborhood, location.city, location.state].filter(Boolean).join(' - ');

      return [
        location.address,
        region,
      ]
        .filter(Boolean)
        .join('\n');
    }

    calculateDistance(originLatitude, originLongitude, targetLatitude, targetLongitude) {
      const earthRadius = 6371;
      const latitudeDelta = this.toRadians(targetLatitude - originLatitude);
      const longitudeDelta = this.toRadians(targetLongitude - originLongitude);
      const origin = this.toRadians(originLatitude);
      const target = this.toRadians(targetLatitude);
      const value =
        Math.sin(latitudeDelta / 2) * Math.sin(latitudeDelta / 2) +
        Math.cos(origin) * Math.cos(target) * Math.sin(longitudeDelta / 2) * Math.sin(longitudeDelta / 2);

      return earthRadius * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
    }

    formatDistance(distance) {
      if (distance < 1) return `${Math.round(distance * 1000)} m`;

      return `${distance.toFixed(distance < 10 ? 1 : 0).replace('.', ',')} km`;
    }

    formatCep(value) {
      const digits = this.onlyDigits(value).slice(0, 8);

      if (digits.length <= 5) return digits;

      return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    }

    onlyDigits(value) {
      return String(value || '').replace(/\D/g, '');
    }

    normalize(value) {
      return this.toText(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ');
    }

    normalizeState(state, stateName) {
      const normalizedState = this.normalize(state).toUpperCase();
      const normalizedStateName = this.normalize(stateName);

      if (normalizedState && BRAZIL_STATES[normalizedState]) return BRAZIL_STATES[normalizedState];
      if (normalizedStateName) return normalizedStateName;

      return this.normalize(state);
    }

    toCoordinate(value) {
      if (typeof value === 'number') return value;

      const normalizedValue = String(value ?? '').trim().replace(',', '.');
      if (!normalizedValue) return NaN;

      const parsed = Number(normalizedValue);
      return Number.isFinite(parsed) ? parsed : NaN;
    }

    toRadians(value) {
      return (value * Math.PI) / 180;
    }

    toText(value) {
      return String(value || '').trim();
    }

    sortByName(locations) {
      return [...locations].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
    }
  }

  customElements.define('ci-store-locator', CIStoreLocator);
})();
